import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, ArrowRight, Bell, CheckCircle, Clock, CreditCard, Eye,
  FilePlus, FileText, KeyRound, LogIn, Mail, Search, Send, Shield,
  ThumbsDown, ThumbsUp, UserCheck, UserPlus, X, XCircle,
} from 'lucide-react';
import SiteNav from './SiteNav';
import PlanInterestModal from './PlanInterestModal';
import { NAV_LINKS } from '../navLinks';
import './HelpPage.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

/* Concept diagrams, not product screenshots or recordings - a stranger
   evaluating the platform should understand how a flow works without the
   page doubling as a click-by-click walkthrough of the real UI. The
   detailed, video-based walkthroughs live in the authenticated product
   instead, where only real customers see them. */
const STEP_ICONS = {
  mail: Mail, userPlus: UserPlus, check: CheckCircle, x: XCircle,
  fileText: FileText, send: Send, shield: Shield, clock: Clock,
  creditCard: CreditCard, eye: Eye, userCheck: UserCheck, filePlus: FilePlus,
  logIn: LogIn, key: KeyRound, bell: Bell,
};

const PLACEHOLDER_EXAMPLES = [
  'How do I create a campaign as a student?',
  'How can I support a student?',
  'Who can register as a student?',
  'How do I approve a campaign as institution staff?',
  'How do I reset my password?',
];

const TYPE_SPEED_MS = 55;
const DELETE_SPEED_MS = 30;
const PAUSE_AFTER_TYPE_MS = 1400;
const PAUSE_AFTER_DELETE_MS = 400;

/* A Google-style animated placeholder - types out an example question,
   holds it briefly, deletes it, and moves to the next one - so someone
   who lands on a blank search box understands at a glance that typing a
   real question (not a category) is the intended action. Only runs
   while the field is genuinely empty and unfocused, so it never fights
   with something the user is actually typing. */
function useTypewriterPlaceholder(active) {
  const [text, setText] = useState('');

  useEffect(() => {
    if (!active) {
      setText('');
      return;
    }

    let exampleIndex = 0;
    let charIndex = 0;
    let deleting = false;
    let timeoutId;

    const tick = () => {
      const current = PLACEHOLDER_EXAMPLES[exampleIndex];

      if (!deleting) {
        charIndex += 1;
        setText(current.slice(0, charIndex));
        if (charIndex === current.length) {
          deleting = true;
          timeoutId = setTimeout(tick, PAUSE_AFTER_TYPE_MS);
          return;
        }
        timeoutId = setTimeout(tick, TYPE_SPEED_MS);
      } else {
        charIndex -= 1;
        setText(current.slice(0, charIndex));
        if (charIndex === 0) {
          deleting = false;
          exampleIndex = (exampleIndex + 1) % PLACEHOLDER_EXAMPLES.length;
          timeoutId = setTimeout(tick, PAUSE_AFTER_DELETE_MS);
          return;
        }
        timeoutId = setTimeout(tick, DELETE_SPEED_MS);
      }
    };

    timeoutId = setTimeout(tick, TYPE_SPEED_MS);
    return () => clearTimeout(timeoutId);
  }, [active]);

  return text;
}

/* Context is written from the Student, Supporter, and Institution module
   docs - what each action is for and why it works the way it does, not
   a click-by-click walkthrough (the video covers that). */
const HELP_CATEGORIES = [
  {
    id: 'institution',
    label: 'For Institutions',
    items: [
      {
        slug: 'institution-invite-staff',
        title: 'Inviting a Staff Member',
        popular: true,
        keywords: ['add staff', 'add a team member', 'invite operations manager', 'invite staff assistant', 'give someone else access', 'add an admin'],
        context: "As an Admin, you can invite other people from your team, like an Operations Manager or Staff Assistant, to help manage campaigns and students. Add them from your dashboard with their name, email, and role, they'll get an invite by email to set up their own account.",
        steps: [
          { icon: 'userPlus', label: 'Admin adds their name, email, and role' },
          { icon: 'mail', label: 'They get an email invite' },
          { icon: 'check', label: 'They set up their own account' },
        ],
      },
      {
        slug: 'institution-staff-signup',
        title: 'Completing Staff Sign-Up from an Email Invite',
        keywords: ['accept staff invite', 'staff invite email', 'set up my staff account', 'join a team'],
        context: "Once your Admin invites you, you'll get an email to finish setting up your account. After that, you're in and ready to go. You'll only ever see your own institution's information, never anyone else's.",
        steps: [
          { icon: 'mail', label: 'Open your invite email' },
          { icon: 'userCheck', label: 'Set up your account' },
          { icon: 'shield', label: "See only your institution's data" },
        ],
      },
      {
        slug: 'institution-invite-student',
        title: 'Inviting a Student',
        keywords: ['start a campaign for a student', 'add a student', 'create campaign on behalf of student', 'sign up a student'],
        context: "Sometimes your team knows a student needs help before that student has even signed up. You can start their campaign and send them an invite by email - but it's still their story to tell, not yours, so it stays private and off the platform until the student joins and personally agrees to let supporters see it.",
        steps: [
          { icon: 'filePlus', label: 'Institution starts the campaign' },
          { icon: 'mail', label: 'Student gets an invite' },
          { icon: 'eye', label: 'Stays private until student agrees' },
        ],
      },
      {
        slug: 'institution-create-campaign',
        title: 'Creating a Campaign as an Institution',
        popular: true,
        keywords: ['how to create a campaign', 'start a campaign', 'new campaign', 'start a fundraiser', 'set up a fundraising campaign', 'make a campaign'],
        context: "Your team can start a fundraising campaign for a student instead of waiting for the student to do it themselves. If the student doesn't have an account yet, the campaign is created but stays hidden from supporters until the student signs up and agrees to share their information.",
        steps: [
          { icon: 'filePlus', label: 'Institution creates the campaign' },
          { icon: 'eye', label: 'Hidden until student joins' },
          { icon: 'check', label: 'Student agrees to share info' },
        ],
      },
      {
        slug: 'institution-approve-campaign',
        title: 'Approving a Campaign',
        popular: true,
        keywords: ['approve a campaign', 'publish a campaign', 'review a campaign', 'accept a campaign request', 'make a campaign live'],
        context: "Every campaign a student sends in has to be checked before it goes live. Their details, documents, and how much they're asking for are all shown together in one place. Only an Admin can approve a campaign, so nothing gets published without someone responsible signing off. The student gets an email as soon as it's approved.",
        steps: [
          { icon: 'fileText', label: 'Student submits campaign' },
          { icon: 'userCheck', label: 'Admin reviews details and documents' },
          { icon: 'check', label: 'Approved, student notified' },
        ],
      },
      {
        slug: 'institution-reject-campaign',
        title: 'Rejecting a Campaign',
        keywords: ['reject a campaign', 'decline a campaign', 'turn down a campaign', 'deny a campaign request'],
        context: "Not every campaign is ready to go live, and turning one down isn't a silent no. An Admin has to explain why, and that reason gets emailed straight to the student, so they always know what to fix.",
        steps: [
          { icon: 'fileText', label: 'Student submits campaign' },
          { icon: 'x', label: 'Admin declines with a reason' },
          { icon: 'mail', label: 'Student gets the reason by email' },
        ],
      },
      {
        slug: 'institution-close-campaign',
        title: "Closing a Campaign on a Student's Behalf",
        keywords: ['close a campaign', 'end a campaign', 'stop a campaign', 'cancel a student campaign', 'deactivate a campaign'],
        context: "Sometimes a campaign no longer needs to stay open, maybe the student's goal changed, or the institution decided to handle things differently. An Admin or Operations Manager can close it from the campaign's details page, with a short reason for the record. Once closed, the campaign stops accepting new contributions, but nothing already raised is affected.",
      },
      {
        slug: 'institution-plans-upgrade',
        title: 'Choosing the Right Plan for Your Institution',
        popular: true,
        keywords: ['upgrade plan', 'change subscription', 'pricing', 'start free trial', 'how much does it cost', 'campaign limit', 'fundraising limit'],
        context: "Every institution starts on the full platform, no matter which plan you're on. Plans only change how many campaigns you can run and how much you can raise at once, so you're never locked out of a core feature. Most institutions start on the free Trial to get a feel for how things work, then move to Standard or Premium as more students join and campaigns grow. There's no paperwork or waiting period to move up, just tell us and our team gets it sorted.",
        cta: { label: 'Start your free trial', action: 'openTrialModal' },
      },
      {
        slug: 'general-reset-password',
        title: 'Resetting Your Password',
        popular: true,
        keywords: ['forgot password', 'change password', "can't log in", 'reset login', 'locked out of my account'],
        context: "Go to your institution's sign-in page and select \"Forgot password.\" Enter your email and we'll send you a link to set a new one, no need to wait on anyone else to do it for you.",
        steps: [
          { icon: 'key', label: 'Select "Forgot password"' },
          { icon: 'mail', label: 'Get a reset link by email' },
          { icon: 'check', label: 'Set a new password' },
        ],
      },
    ],
  },
  {
    id: 'student',
    label: 'For Students',
    items: [
      {
        slug: 'student-register',
        title: 'Registering as a Student',
        popular: true,
        keywords: ['sign up as a student', 'create a student account', 'start my campaign', 'apply for funding', 'how to create a campaign as a student'],
        context: "If your institution hasn't already started a campaign for you, this is where you begin. Fill in your details, your course information, and upload any documents needed. If you're under 18, a parent or guardian will need to confirm before you can continue. Once you're done, you'll be guided straight into creating your first campaign.",
        steps: [
          { icon: 'fileText', label: 'Fill in your details and documents' },
          { icon: 'shield', label: 'Guardian confirms if under 18' },
          { icon: 'filePlus', label: 'Create your first campaign' },
        ],
      },
      {
        slug: 'student-complete-registration',
        title: 'Completing Registration for an Invited Campaign',
        keywords: ['finish signing up', 'accept my campaign invite', 'claim my campaign', 'invited by my institution'],
        context: "If your institution has already started a campaign for you, all you need to do is finish signing up and agree to share your information. As soon as you do both, your campaign goes live for supporters to see. Nothing else needed.",
        steps: [
          { icon: 'mail', label: 'Open your institution\'s invite' },
          { icon: 'userCheck', label: 'Finish sign-up, agree to share info' },
          { icon: 'check', label: 'Campaign goes live' },
        ],
      },
      {
        slug: 'student-sign-in',
        title: 'Signing In as a Student',
        keywords: ['log in as a student', "can't sign in", 'student login', 'access my dashboard'],
        context: "The first time you sign in, we check whether your institution has already set up a campaign for you. If they have, you'll land straight on your dashboard with your details already filled in. If not, you'll be guided to register first.",
        steps: [
          { icon: 'logIn', label: 'Sign in' },
          { icon: 'fileText', label: 'We check for an existing campaign' },
          { icon: 'check', label: 'Land on your dashboard, or register' },
        ],
      },
      {
        slug: 'student-terminate-campaign',
        title: 'Ending a Campaign You No Longer Need',
        popular: true,
        keywords: ['end my campaign', 'close my campaign', 'stop my campaign', 'cancel my campaign', "don't need funding anymore"],
        context: "If your situation changes and you no longer need to raise funds, you can end your campaign yourself from your dashboard. You'll be asked for a short reason, and once it's confirmed, the campaign stops accepting new contributions right away. Anything already raised stays exactly as it was, this only closes the campaign to new support.",
      },
    ],
  },
  {
    id: 'supporter',
    label: 'For Supporters',
    items: [
      {
        slug: 'supporter-make-payment',
        title: 'Making a Payment',
        popular: true,
        keywords: ['how to donate', 'contribute to a campaign', 'pay a student', 'payment failed', 'payment methods', 'donation not going through'],
        context: "When you contribute, you'll be taken to a secure payment page where you can pay by card, UPI, net banking, or wallet. If the payment goes through, you'll get a confirmation on screen and by email, and it counts toward the student's goal right away. If it doesn't go through, you'll see that too, and you can just try again.",
        steps: [
          { icon: 'creditCard', label: 'Pay by card, UPI, net banking, or wallet' },
          { icon: 'check', label: 'Confirmed on screen and by email' },
          { icon: 'bell', label: 'Counts toward the goal right away' },
        ],
      },
    ],
  },
  {
    id: 'general',
    label: 'General Questions',
    items: [
      {
        slug: 'general-who-can-register',
        title: 'Who Can Register as a Student?',
        popular: true,
        keywords: ['who can join', 'am i eligible', 'which students can sign up', 'can i register', 'is my college on bulandipe', 'eligibility'],
        context: "You register through your own school or college, using the link or portal they give you. If you're not sure whether your institution has one set up yet, the quickest way to find out is to ask them directly, or reach out to us and we'll point you in the right direction.",
      },
      {
        slug: 'general-what-happens-after-registering',
        title: 'What Happens After I Register and Submit a Campaign?',
        popular: true,
        keywords: ['what happens next', 'is my campaign live', 'when does my campaign go live', 'approval process', 'review process', 'why is my campaign not showing'],
        context: "Registering and submitting your campaign is just the first step, it doesn't go live right away. Your institution's staff review everything first: your details, your documents, and what you're asking for. Only once an Admin approves it does your campaign become visible to supporters. You'll get an email either way, whether it's approved or sent back with a reason so you know what to fix.",
        steps: [
          { icon: 'fileText', label: 'Submit your details and documents' },
          { icon: 'userCheck', label: 'Staff review everything' },
          { icon: 'check', label: 'Approved and visible, or sent back with a reason' },
        ],
      },
      {
        slug: 'general-is-my-information-safe',
        title: 'Who Can See My Personal Information and Documents?',
        popular: true,
        keywords: ['is my data safe', 'who sees my documents', 'privacy', 'is my information private', 'who can view my id'],
        context: "Only your own institution's staff can see your documents, things like your ID and fee details are never shown to supporters or the public, and never visible to any other institution on the platform. They're there purely so your institution can verify you and approve your campaign responsibly.",
      },
      {
        slug: 'general-under-18',
        title: "What If I'm Under 18?",
        keywords: ['minor', 'under 18', 'guardian consent', 'parent approval', 'age limit', 'am i too young'],
        context: "You can still register, you'll just need a parent or guardian to confirm first. If you're under 18, they'll get an email asking them to approve before your registration goes through. It's a one-time step, once they've confirmed, everything else works exactly the same as it would for anyone else.",
      },
    ],
  },
];

const ALL_ARTICLES = HELP_CATEGORIES.flatMap(cat =>
  cat.items.map(item => ({ ...item, categoryId: cat.id, categoryLabel: cat.label }))
);

const POPULAR_ARTICLES = ALL_ARTICLES.filter(item => item.popular);

const STOP_WORDS = new Set(['a', 'an', 'the', 'to', 'for', 'of', 'my', 'do', 'does', 'is', 'how', 'i', 'on', 'in']);
const significantWords = (text) =>
  text.toLowerCase().split(/[^a-z0-9]+/).filter(w => w.length > 1 && !STOP_WORDS.has(w));

/* People rarely type the exact wording an article uses (someone asking
   "how do I create a campaign" won't literally match "Creating a
   Campaign as an Institution" as a substring). Each article carries a
   list of alternate phrasings (`keywords`) real users might type, and
   this scores every field - an exact substring match ranks highest,
   shared significant words are a fallback - instead of requiring the
   query to appear verbatim somewhere in the text. */
function searchArticles(query) {
  const needle = query.toLowerCase();
  const queryWords = significantWords(query);
  // A single shared word (e.g. "campaign") appears in almost every
  // article's keywords, so word-overlap alone needs a real majority of
  // the query's words to count as a match - otherwise every query about
  // campaigns returns every campaign-related article.
  const overlapThreshold = queryWords.length <= 1 ? 1 : Math.ceil(queryWords.length * 0.6);

  const scored = ALL_ARTICLES.map(item => {
    const title = item.title.toLowerCase();
    const keywordList = item.keywords || [];
    let score = 0;

    if (title.includes(needle)) score += 100;
    if (keywordList.some(k => k.toLowerCase().includes(needle))) score += 80;
    if (item.context.toLowerCase().includes(needle)) score += 40;

    if (queryWords.length > 0) {
      const haystack = [title, ...keywordList.map(k => k.toLowerCase())].join(' ');
      const haystackWords = new Set(significantWords(haystack));
      const overlap = queryWords.filter(w => haystackWords.has(w)).length;
      if (overlap >= overlapThreshold) score += overlap * 15;
    }

    return { item, score };
  });

  return scored
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ item }) => item);
}

function useTrialModal() {
  const [planModal, setPlanModal] = useState(false);

  const handleArticleCta = (cta) => {
    if (cta.action === 'openTrialModal') setPlanModal(true);
  };

  const handlePlanInterestSubmit = async (data) => {
    await axios.post(`${API_URL}/api/public/plan-interest`, {
      institutionName: data.institutionName,
      contactPerson: data.contactPerson,
      email: data.email,
      phone: data.phone,
      selectedPlan: data.selectedPlan,
      message: data.message || undefined,
      turnstileToken: data.turnstileToken,
    });
  };

  const modal = planModal && (
    <PlanInterestModal plan="Trial" onClose={() => setPlanModal(false)} onSubmit={handlePlanInterestSubmit} />
  );

  return { handleArticleCta, modal };
}

/* Did-this-help feedback: a lightweight yes/no, not a survey. "No" goes
   straight to Contact Us - never back to the search box - so a wrong
   answer never dead-ends the person who just spent time reading it. */
function AnswerFeedback({ slug }) {
  const [answered, setAnswered] = useState(null);

  if (answered === 'yes') {
    return <p className="hp-feedback-done">Glad that helped.</p>;
  }
  if (answered === 'no') {
    return (
      <p className="hp-feedback-done">
        Sorry that didn't help -{' '}
        <a
          href="mailto:contact@bulandipe.com?subject=Help%20with%20BulandiPe&body=Hi%2C%20I'm%20having%20an%20issue%20with...%0A%0AI%20wanted%20to%20talk%20about..."
          className="hp-feedback-link"
        >
          contact us directly
        </a>{' '}
        and we'll sort it out.
      </p>
    );
  }
  return (
    <div className="hp-feedback" data-testid={`help-feedback-${slug}`}>
      <span>Did this answer your question?</span>
      <button onClick={() => setAnswered('yes')} aria-label="Yes, this helped" data-testid={`help-feedback-yes-${slug}`}>
        <ThumbsUp size={14} aria-hidden="true" /> Yes
      </button>
      <button onClick={() => setAnswered('no')} aria-label="No, this didn't help" data-testid={`help-feedback-no-${slug}`}>
        <ThumbsDown size={14} aria-hidden="true" /> No
      </button>
    </div>
  );
}

const SEQUENCE_AUTOPLAY_MS = 3200;

/* Replaces the old static icon-row diagram. Shows one step at a time as a
   sliding card, in place of a video - the shape of a flow (who does what,
   in what order) without walking through the actual product screens, so
   it stays genuinely explanatory without giving a competitor anything to
   study frame by frame. Auto-advances so a first-time visitor sees the
   whole flow without touching anything, but Back/Next and the dots hand
   full control to anyone who wants to move at their own pace. */
function StepSequence({ steps, title }) {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState('forward');
  const [paused, setPaused] = useState(false);
  const prefersReducedMotion = useMemo(
    () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
    []
  );

  useEffect(() => {
    if (paused || prefersReducedMotion || steps.length <= 1) return undefined;
    const timer = setTimeout(() => {
      setDirection('forward');
      setIndex(current => (current + 1) % steps.length);
    }, SEQUENCE_AUTOPLAY_MS);
    return () => clearTimeout(timer);
  }, [index, paused, prefersReducedMotion, steps.length]);

  const goTo = (nextIndex, dir) => {
    setPaused(true);
    setDirection(dir);
    setIndex(nextIndex);
  };

  const step = steps[index];
  const Icon = STEP_ICONS[step.icon];

  return (
    <div
      className="hp-sequence"
      role="group"
      aria-roledescription="carousel"
      aria-label={title ? `${title}, step by step` : 'Step by step'}
    >
      <div className="hp-sequence-track" role="list">
        {steps.map((_, i) => (
          <React.Fragment key={i}>
            <button
              type="button"
              className={`hp-sequence-dot${i === index ? ' is-active' : ''}${i < index ? ' is-done' : ''}`}
              aria-label={`Go to step ${i + 1} of ${steps.length}`}
              aria-current={i === index}
              onClick={() => goTo(i, i > index ? 'forward' : 'back')}
            />
            {i < steps.length - 1 && (
              <span className={`hp-sequence-track-line${i < index ? ' is-done' : ''}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      <div className="hp-sequence-viewport">
        <div key={index} className={`hp-sequence-frame hp-sequence-frame--${direction}`}>
          <div className="hp-sequence-icon"><Icon size={20} aria-hidden="true" /></div>
          <div className="hp-sequence-text">
            <p className="hp-sequence-title">{step.label}</p>
            {step.detail && <p className="hp-sequence-detail">{step.detail}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

/* Clicking a title expands the full answer right where it is - no page
   navigation, no breadcrumb to reorient around. Only one item is open
   per list at a time so the page doesn't refill with content the person
   already dismissed. */
function AnswerItem({ item, eyebrow, isOpen, onToggle, onCtaClick }) {
  const rootRef = useRef(null);

  // Bring the question up to the center of the screen once it expands,
  // regardless of where on the page it was clicked, so the answer that
  // just appeared is always the thing actually in view - not cut off at
  // the bottom of the viewport.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (isOpen) {
      rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isOpen]);

  return (
    <div ref={rootRef} className="hp-answer" data-testid={`help-answer-${item.slug}`}>
      <button
        className="hp-answer-trigger"
        onClick={() => onToggle(item.slug)}
        aria-expanded={isOpen}
        data-testid={`help-answer-trigger-${item.slug}`}
      >
        <span className="hp-answer-trigger-text">
          {eyebrow && <span className="hp-answer-eyebrow">{eyebrow}</span>}
          <span className="hp-answer-title">{item.title}</span>
        </span>
        <ArrowRight size={16} className="hp-answer-chevron" aria-hidden="true" />
      </button>

      {isOpen && (
        <div className="hp-answer-body">
          <p className="hp-answer-context">{item.context}</p>
          {item.cta && (
            <button className="hp-article-cta" onClick={() => onCtaClick(item.cta)} data-testid={`help-article-cta-${item.slug}`}>
              {item.cta.label}
              <ArrowRight size={14} aria-hidden="true" />
            </button>
          )}
          {item.steps && <StepSequence steps={item.steps} title={item.title} />}
          <AnswerFeedback slug={item.slug} />
        </div>
      )}
    </div>
  );
}

export default function HelpPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Lets the SaaS product deep-link straight into relevant results (e.g.
  // an institution admin's "Help" button links to /help?q=campaign
  // instead of dumping them on the unfiltered list) - matches the page's
  // own search-first design instead of reintroducing category browsing.
  const [query, setQuery] = useState(() => searchParams.get('q') || '');
  // Keyed per list (search / popular / each category) rather than one
  // shared slug - the same article appears in more than one list (e.g. a
  // popular question also sits in its category's browse list further
  // down), and a single global "open" value made opening it in one list
  // silently open its duplicate elsewhere too, which then won whichever
  // scroll-into-view happened last and looked like the page had jumped
  // away on its own.
  const [openByList, setOpenByList] = useState({});
  const [searchFocused, setSearchFocused] = useState(false);
  const animatedPlaceholder = useTypewriterPlaceholder(!searchFocused && !query);
  const { handleArticleCta, modal } = useTrialModal();

  const goBack = () => {
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate('/');
  };

  const toggleOpen = (listId, slug) =>
    setOpenByList(current => ({ ...current, [listId]: current[listId] === slug ? null : slug }));

  const trimmedQuery = query.trim();
  const searchResults = useMemo(() => {
    if (!trimmedQuery) return null;
    return searchArticles(trimmedQuery);
  }, [trimmedQuery]);

  return (
    <div className="hp-root">
      <SiteNav navLinks={NAV_LINKS} activeId="help" forceSolid contactHref="mailto:contact@bulandipe.com" />

      <header className="hp-header">
        <div className="hp-header-inner">
          <button className="hp-back-btn" onClick={goBack} data-testid="help-back-btn">
            <ArrowLeft size={15} aria-hidden="true" />
            Back
          </button>
          <div className="bp-eyebrow">Help Center</div>
          <h1 className="hp-headline">What do you need help with?</h1>

          {/* Search is the primary action, not a corner input - typing
              filters results live, right here, with no submit step and
              no separate results page. */}
          <div className="hp-hero-search">
            <Search size={18} className="hp-search-icon" aria-hidden="true" />
            <input
              type="text"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setOpenByList(current => ({ ...current, search: null })); }}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              placeholder={searchFocused ? 'Type your question…' : ''}
              className="hp-search-input"
              aria-label="Search help articles"
              data-testid="help-search-input"
            />
            {!query && !searchFocused && (
              <span className="hp-search-animated-placeholder" aria-hidden="true">
                {animatedPlaceholder}
                <span className="hp-search-caret" />
              </span>
            )}
            {query && (
              <button className="hp-search-clear" onClick={() => setQuery('')} aria-label="Clear search">
                <X size={15} aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="hp-body">
        {searchResults ? (
          searchResults.length > 0 ? (
            <div className="hp-answer-list" data-testid="help-search-results">
              <p className="hp-search-summary">
                {searchResults.length === 1
                  ? '1 result found'
                  : `${searchResults.length} results found`}
              </p>
              {searchResults.map(item => (
                <AnswerItem
                  key={item.slug}
                  item={item}
                  eyebrow={item.categoryLabel}
                  isOpen={searchResults.length === 1 ? true : openByList.search === item.slug}
                  onToggle={(slug) => toggleOpen('search', slug)}
                  onCtaClick={handleArticleCta}
                />
              ))}
            </div>
          ) : (
            <div className="hp-empty-state" data-testid="help-search-empty">
              <p className="hp-empty-title">No results for "{trimmedQuery}"</p>
              <p className="hp-empty-sub">
                Try different words, or{' '}
                <a href="mailto:contact@bulandipe.com?subject=Help%20with%20BulandiPe&body=Hi%2C%20I'm%20having%20an%20issue%20with...%0A%0AI%20wanted%20to%20talk%20about...">
                  contact us directly
                </a>.
              </p>
              <button className="hp-empty-clear" onClick={() => setQuery('')}>Clear search</button>
            </div>
          )
        ) : (
          <>
            {/* Zero-click surface: the most common questions across all
                three audiences, visible the instant the page loads, no
                category choice required first. */}
            <section className="hp-popular">
              <p className="hp-list-label">Popular questions</p>
              <div className="hp-answer-list">
                {POPULAR_ARTICLES.map(item => (
                  <AnswerItem
                    key={item.slug}
                    item={item}
                    eyebrow={item.categoryLabel}
                    isOpen={openByList.popular === item.slug}
                    onToggle={(slug) => toggleOpen('popular', slug)}
                    onCtaClick={handleArticleCta}
                  />
                ))}
              </div>
            </section>

            {/* Everything else, for the rare case someone wants to browse
                rather than search - grouped by audience but still just
                titles that expand in place, never a new page. */}
            {HELP_CATEGORIES.map(cat => (
              <section key={cat.id} className="hp-browse-section">
                <p className="hp-list-label">{cat.label}</p>
                <div className="hp-answer-list">
                  {cat.items.map(item => (
                    <AnswerItem
                      key={item.slug}
                      item={item}
                      isOpen={openByList[cat.id] === item.slug}
                      onToggle={(slug) => toggleOpen(cat.id, slug)}
                      onCtaClick={handleArticleCta}
                    />
                  ))}
                </div>
              </section>
            ))}
          </>
        )}
      </div>

      <section className="hp-cta">
        <div className="hp-cta-inner">
          <h2 className="hp-cta-title">Still stuck?</h2>
          <p className="hp-cta-sub">Can't find what you're looking for? Our team can help directly.</p>
          <a
            href="mailto:contact@bulandipe.com?subject=Help%20with%20BulandiPe&body=Hi%2C%20I'm%20having%20an%20issue%20with...%0A%0AI%20wanted%20to%20talk%20about..."
            className="hp-cta-btn"
          >
            Contact Us
          </a>
        </div>
      </section>

      {modal}
    </div>
  );
}
